import { useEffect, useRef, useState } from 'react';
import { register, call } from './funcStore';
import { mockVtubers } from './data/mockVtubers';
import { mockLiveStreams } from './data/mockLiveStreams';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import VTuberDetailPage from './pages/VTuberDetailPage';
import AdminApprovePage from './pages/AdminApprovePage';
import ContactPage from './pages/ContactPage';
import CreateVtuberPage from './pages/CreateVtuberPage';
import { getVtuberById, getTrendingVideos } from './api/mockApi';
import { getLiveNow } from './api/liveApi';
import { logAction } from './api/logApi';
import { recordTagInterest } from './utils/userPreference';
import { getActiveFilters } from './utils/searchFilters';
import { FILTER_GROUPS } from './data/filterGroups';

export const SCREEN_BEFORE_INIT = 'beforeInit';
export const SCREEN_LIVE = 'live';
export const SCREEN_TREND = 'trend';
export const SCREEN_SEARCH = 'search';
export const SCREEN_DETAIL = 'detail';
export const SCREEN_ADMIN = 'admin';
export const SCREEN_CONTACT = 'contact';
export const SCREEN_CREATE = 'create';

const HOME_SCREENS = [SCREEN_LIVE, SCREEN_TREND];

const SCREEN_LABELS = {
  [SCREEN_LIVE]: '配信中',
  [SCREEN_TREND]: '急上昇',
  [SCREEN_SEARCH]: '検索',
  [SCREEN_DETAIL]: 'VTuber詳細',
  [SCREEN_ADMIN]: '属性承認',
  [SCREEN_CONTACT]: 'お問い合わせ',
  [SCREEN_CREATE]: 'ページ作成',
};

function App() {
  const [screenId, setScreenId] = useState(SCREEN_BEFORE_INIT);

  const screenIdRef = useRef(screenId);
  screenIdRef.current = screenId;

  const dataRef = useRef({
    selectedVtuberId: null,
    history: [],
    homeClickedTag:"",
    homeTab: "live",
    mobile:"pc",
    isAdmin: window.location.search.includes('isAdminKey'),
    contactContext: null,
  });

  // お問い合わせ画面へ渡す「どの画面から来たか」の情報を組み立てる
  // detail画面ならVTuberのid/name、search画面なら検索条件を添える
  const buildContactContext = () => {
    const originScreenId = screenIdRef.current;
    const context = { screen: SCREEN_LABELS[originScreenId] ?? originScreenId };

    if (originScreenId === SCREEN_DETAIL) {
      const vtuber = call('vtuberDetailPage.getVtuber');
      if (vtuber) {
        context.vtuberId = vtuber.id;
        context.vtuberName = vtuber.name;
      }
    }

    if (originScreenId === SCREEN_SEARCH) {
      const filters = call('searchPage.getAllFilter');
      if (filters) {
        const activeFilters = getActiveFilters(filters, FILTER_GROUPS);
        context.conditions = activeFilters.map(
          ({ label, value }) => `${label}: ${value}`,
        );
      }
    }

    return context;
  };

  const pushHistory = (newScreenId) => {
    const entry = {
      screenId: newScreenId,
      selectedVtuberId: dataRef.current.selectedVtuberId,
    };

    dataRef.current.history.push(entry);

    const index = dataRef.current.history.length - 1;
    window.history.pushState({ index }, '');
  };

  const handlePopState = (event) => {
    const index = event.state ? event.state.index : null;

    if (index === null || index === undefined) {
      return;
    }

    const entry = dataRef.current.history[index];

    if (!entry) {
      return;
    }

    dataRef.current.selectedVtuberId = entry.selectedVtuberId;

    if (HOME_SCREENS.includes(entry.screenId)) {
      dataRef.current.homeTab = entry.screenId;
    }

    setScreenId(entry.screenId);
  };

  const init = () => {

    register('app.navigateToHome', () => {
      logAction('navigate_home');
      pushHistory(SCREEN_LIVE);
      setScreenId(SCREEN_LIVE);
    });

    register('app.navigateToLive', () => {
      logAction('navigate_live');
      dataRef.current.homeTab = SCREEN_LIVE;
      pushHistory(SCREEN_LIVE);
      setScreenId(SCREEN_LIVE);
    });

    register('app.navigateToTrend', () => {
      logAction('navigate_trend');
      dataRef.current.homeTab = SCREEN_TREND;
      pushHistory(SCREEN_TREND);
      setScreenId(SCREEN_TREND);
    });

    register('app.navigateToSearch', () => {
      logAction('navigate_search');
      pushHistory(SCREEN_SEARCH);
      setScreenId(SCREEN_SEARCH);
    });

    register('app.navigateToDetail', (vtuberId) => {
      logAction('navigate_detail', { vtuberId });
      dataRef.current.selectedVtuberId = vtuberId;
      pushHistory(SCREEN_DETAIL);
      setScreenId(SCREEN_DETAIL);
    });

    register('app.navigateToAdmin', () => {
      logAction('navigate_admin');
      pushHistory(SCREEN_ADMIN);
      setScreenId(SCREEN_ADMIN);
    });

    register('app.navigateToContact', () => {
      logAction('navigate_contact');
      dataRef.current.contactContext = buildContactContext();
      pushHistory(SCREEN_CONTACT);
      setScreenId(SCREEN_CONTACT);
    });

    register('app.navigateToCreate', () => {
      logAction('navigate_create');
      pushHistory(SCREEN_CREATE);
      setScreenId(SCREEN_CREATE);
    });

    register('app.getContactContext', () => dataRef.current.contactContext);

    register('app.isAdmin', () => dataRef.current.isAdmin);

    register('app.searchByTag', (tag) => {
      logAction('tag_click', { tag });
      recordTagInterest(tag);

      if (HOME_SCREENS.includes(screenIdRef.current)) {
        dataRef.current.homeClickedTag = tag
      }
      if (screenIdRef.current === SCREEN_SEARCH) {
        call('searchPage.setFilterByTag', tag);
        return;
      }

      pushHistory(SCREEN_SEARCH);
      setScreenId(SCREEN_SEARCH);
    });

    register('app.getClickedTag',()=>{
      const tag = dataRef.current.homeClickedTag
      dataRef.current.homeClickedTag = ""
      return tag
    })

    register('app.getSelectedVtuberId', getSelectVtuberId);

    register('app.getVtuberById', async(id) =>
      await getVtuberById(id)
    );

    register('app.getLiveStreamById', (id) =>
      mockLiveStreams.find((stream) => stream.id === id)
    );

    register('app.getTrendingVideos', (range) => getTrendingVideos(range));

    register('app.getLiveNow', (tags) => getLiveNow(tags));

    register('app.getState',()=>screenIdRef.current)

    window.addEventListener('popstate', handlePopState);

    // 広告などから ?create 付きで来た場合は、最初からページ作成画面を表示する
    const initialScreenId = new URLSearchParams(window.location.search).has(
      'create',
    )
      ? SCREEN_CREATE
      : SCREEN_LIVE;

    dataRef.current.history.push({
      screenId: initialScreenId,
      selectedVtuberId: null,
    });
    window.history.replaceState({ index: 0 }, '');

    setScreenId(initialScreenId);
  };

  const getSelectVtuberId=()=>{
    return dataRef.current.selectedVtuberId
  }

  useEffect(() => {
    if (screenId === SCREEN_BEFORE_INIT) {
      init();
    }
  }, [screenId]);

  useEffect(() => {
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  if (screenId === SCREEN_BEFORE_INIT) {
    return null;
  }

  let screen = <HomePage />;

  if (screenId === SCREEN_ADMIN) {
    screen = <AdminApprovePage />;
  } else if (screenId === SCREEN_CONTACT) {
    screen = <ContactPage />;
  } else if (screenId === SCREEN_CREATE) {
    screen = <CreateVtuberPage />;
  } else if (screenId === SCREEN_DETAIL) {
    screen = <VTuberDetailPage />;
  } else if (screenId === SCREEN_SEARCH) {
    screen = <SearchPage />;
  }

  return (
    <>
      {screen}
    </>
  );
}

export default App;
